import './CommentPage.css'

import axios from 'axios'
import type { ForumModel } from 'database'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams } from 'react-router-dom'

import JoinCommentDiscussionOverlay from '../components/JoinCommentDiscussionOverlay'
import { useLogger } from '../logger'
import { useEventTracker } from '../providers/event-tracker-provider'
import { API_GATEWAY_URL } from '../utils/env'

interface Comment {
  id: string
  content: string
  isAnonymous: boolean
  timestamp: string
}

interface CommentPageData {
  forum: ForumModel
  comment: Comment
}

export default function CommentPage() {
  const { forumName, commentId } = useParams<{ forumName: string; commentId: string }>()
  const [data, setData] = useState<CommentPageData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showOverlay, setShowOverlay] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const logger = useLogger()
  const { tracker: eventTracker, isReady } = useEventTracker()

  // Get forum and comment data from the response
  const forum = data?.forum
  const targetComment = data?.comment

  useEffect(() => {
    if (!forumName || !commentId) {
      setError('Invalid forum or comment ID')
      return
    }

    const fetchCommentData = async () => {
      try {
        logger.info(`Fetching comment data for forum: ${forumName}, comment: ${commentId}`)
        const response = await axios.get(`${API_GATEWAY_URL}/forums/by-comment/${forumName}/${commentId}`)

        if (!response.data) {
          throw new Error('No data received from server')
        }

        logger.info('Comment data:', response.data)

        // The API should return { forum: ForumModel, comment: Comment }
        if (!response.data.forum || !response.data.comment) {
          throw new Error('Invalid response structure from server')
        }

        setData({
          forum: response.data.forum,
          comment: response.data.comment,
        })
        logger.info('Comment data loaded successfully', response.data)
      } catch (err) {
        logger.error('Failed to load comment data:', err)
        setError('Failed to load comment. Please try again.')
      }
    }

    fetchCommentData()
  }, [forumName, commentId, logger])

  // Track page view
  useEffect(() => {
    if (isReady && forumName && commentId) {
      eventTracker.track({
        name: 'Link Comment Page Viewed',
        forumName,
        commentId,
      })
    }
  }, [isReady, forumName, commentId, eventTracker])

  const handleJoinDiscussion = () => {
    eventTracker.track({
      name: 'Link Comment Join Discussion',
      forumName: forumName || '',
      commentId: commentId || '',
    })
    setShowOverlay(true)
  }

  const handleCloseOverlay = () => {
    setShowOverlay(false)
  }

  const handleInputClick = () => {
    handleJoinDiscussion()
  }

  const handleSendClick = () => {
    handleJoinDiscussion()
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value)
    // Immediately show overlay when user tries to type
    if (!showOverlay) {
      handleJoinDiscussion()
    }
  }

  if (error) {
    return (
      <div className="comment-page">
        <div className="comment-container">
          <div className="chat-interface">
            <div className="error-container">
              <div className="error-text">{error}</div>
              <button onClick={() => (window.location.href = '/')} className="retry-button">
                Back to Homepage
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!data || !forum || !targetComment) {
    return (
      <div className="comment-page">
        <div className="comment-container">
          <div className="chat-interface">
            <div className="loading-container">
              <div>Loading...</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="comment-page">
      <div className="comment-container" onClick={handleJoinDiscussion}>
        <Helmet>
          <title>{forum ? `"${forum.statement}" - Comment Discussion | Nugle` : 'Comment Discussion | Nugle'}</title>
          <meta
            name="description"
            content={
              targetComment
                ? `Join the discussion: "${targetComment.content.substring(0, 150)}${
                    targetComment.content.length > 150 ? '...' : ''
                  }"`
                : 'Join anonymous discussions on Nugle'
            }
          />

          {/* Twitter Card meta tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="@nugle_app" />
          <meta name="twitter:creator" content="@nugle_app" />
          <meta name="twitter:title" content={forum ? `"${forum.statement}"` : 'Anonymous Discussion'} />
          <meta
            name="twitter:description"
            content={
              targetComment
                ? `"${targetComment.content.substring(0, 200)}${
                    targetComment.content.length > 200 ? '...' : ''
                  }" - Join the anonymous discussion on Nugle`
                : 'Join anonymous discussions and change perspectives on Nugle'
            }
          />
          <meta name="twitter:image" content={`https://d1yrgwskeg1le6.cloudfront.net/NugleDebateBanner_1.jpg`} />
          <meta
            name="twitter:image:alt"
            content={targetComment ? `AI mediated debate on ${targetComment.content}` : 'Nugle: speak your truth'}
          />

          {/* Open Graph meta tags */}
          <meta property="og:type" content="article" />
          <meta property="og:title" content={forum ? `"${forum.statement}"` : 'Anonymous Discussion'} />
          <meta
            property="og:description"
            content={
              targetComment
                ? `"${targetComment.content.substring(0, 200)}${
                    targetComment.content.length > 200 ? '...' : ''
                  }" - Join the anonymous discussion on Nugle`
                : 'Join anonymous discussions and change perspectives on Nugle'
            }
          />
          <meta property="og:image" content={`https://d1yrgwskeg1le6.cloudfront.net/NugleDebateBanner_1.jpg`} />
          <meta property="og:url" content={window.location.href} />
          <meta property="og:site_name" content="Nugle" />
        </Helmet>

        <div className="chat-interface animate-fade-in">
          {/* Chat Header */}
          <div className="chat-header">
            <div className="chat-title-container">
              <h1 className="chat-title">&quot;{forum.statement}&quot;</h1>
              <p className="chat-subtitle">anonymous debates, mediated by AI</p>
            </div>
          </div>

          {/* Chat Messages */}
          <div className="chat-messages">
            {targetComment && (
              <div className="message-container other-message">
                <div className="message-bubble other-message-bubble">{targetComment.content}</div>
              </div>
            )}

            {/* Show a placeholder for user's response */}
            <div className="message-container my-message">
              <div className="message-bubble my-message-bubble" style={{ opacity: 0.3 }}>
                Your response will appear here...
              </div>
            </div>
          </div>

          {/* Chat Input */}
          <div className="chat-input-container">
            <textarea
              className="chat-input"
              placeholder="Type a message..."
              value={inputValue}
              onChange={handleInputChange}
              onClick={handleInputClick}
              onFocus={handleInputClick}
              rows={1}
            />
            <button className="chat-send-button" onClick={handleSendClick} disabled={!inputValue.trim()}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 21L23 12L2 3V10L17 12L2 14V21Z" fill="currentColor" />
              </svg>
            </button>
          </div>
        </div>

        {/* Join Discussion Button */}
        <div className="join-discussion-container">
          <button className="join-discussion-button" onClick={handleJoinDiscussion}>
            Join the conversation
          </button>
        </div>

        {showOverlay && (
          <JoinCommentDiscussionOverlay
            forumName={forumName || ''}
            commentId={commentId || ''}
            onClose={handleCloseOverlay}
          />
        )}
      </div>
    </div>
  )
}
